import { RouteContext, Routes, RouteStates } from './types';

export const routeSequence: Routes[] = [
  Routes.SERVICEABILITY,
  Routes.PLAN,
  Routes.PERSONAL_INFORMATION,
  Routes.CREDIT_CHECK,
  Routes.ADDONS,
  Routes.DISCLOSURES,
  Routes.SCHEDULE_INSTALLATION,
  Routes.PAYMENT_INFORMATION,
  Routes.ORDER_SUMMARY,
];

export function buildRoutes(context: RouteContext): Routes[] {
  const { skipCreditCheck, skipAddons, skipDisclosures } = context;

  const routes: Routes[] = [...routeSequence];

  return routes.filter((route) => {
    const shouldSkipCreditCheck = skipCreditCheck && route === Routes.CREDIT_CHECK;
    const shouldSkipAddons = skipAddons && route === Routes.ADDONS;
    const shouldSkipDisclosures = skipDisclosures && route === Routes.DISCLOSURES;

    if (shouldSkipCreditCheck || shouldSkipAddons || shouldSkipDisclosures) {
      return false;
    }

    return true;
  });
}

// `reachedRoute` may no longer exist in `activeRoutes` (a flag change can drop a step from the flow),
// so positions are looked up against the canonical `routeSequence` rather than the active one.
// Walks backwards from where the customer got to and returns the nearest step still in their flow.
export function furthestStepInFlow(activeRoutes: RouteSequence, reachedRoute: Routes): Routes {
  const stepsUpToReachedRoute = routeSequence.slice(0, routeSequence.indexOf(reachedRoute) + 1);

  return stepsUpToReachedRoute.reverse().find((step) => activeRoutes.includes(step)) ?? Routes.SERVICEABILITY;
}

export function buildRouteStates(routes: RouteSequence): RouteStates {
  const routeStates: Partial<RouteStates> = {};

  routes.forEach((route, index) => {
    routeStates[route] = {
      order: index,
      Next: routes[index + 1],
      Previous: routes[index - 1],
      Reset: index === routes.length - 1 ? routes[0] : undefined,
    };
  });

  const allRoutes = Object.values(Routes);

  allRoutes.forEach((route) => {
    if (!routeStates[route]) {
      routeStates[route] = {
        order: -1,
      };
    }
  });

  return routeStates as RouteStates;
}

export type RouteSequence = Routes[];
export const NUMBERED_ROUTES: Routes[] = [
  Routes.PERSONAL_INFORMATION,
  Routes.CREDIT_CHECK,
  Routes.ADDONS,
  Routes.DISCLOSURES,
  Routes.SCHEDULE_INSTALLATION,
  Routes.PAYMENT_INFORMATION,
];

function getNumberedRoutesInSequence(routes: RouteSequence): Routes[] {
  return routes.filter((route) => {
    if (!NUMBERED_ROUTES.includes(route) || route === Routes.DISCLOSURES) {
      return false;
    }
    return true;
  });
}

export function getStepNumber(routes: RouteSequence, route: Routes): number {
  if (!NUMBERED_ROUTES.includes(route)) {
    return 0;
  }

  if (route === Routes.DISCLOSURES) {
    return getDisclosuresStepNumber(routes, route);
  }

  const numberedRoutesInSequence = getNumberedRoutesInSequence(routes);
  const index = numberedRoutesInSequence.indexOf(route);

  return index >= 0 ? index + 1 : 0;
}

function getDisclosuresStepNumber(routes: RouteSequence, route: Routes): number {
  const routeIndex = routes.indexOf(route);
  for (let i = routeIndex - 1; i >= 0; i--) {
    const previousRoute = routes[i];
    if (NUMBERED_ROUTES.includes(previousRoute) && previousRoute !== Routes.DISCLOSURES) {
      return getStepNumber(routes, previousRoute);
    }
  }
  return 0;
}

export function getTotalSteps(routes: RouteSequence): number {
  return getNumberedRoutesInSequence(routes).length;
}
